import { useMutation, useQuery } from "@apollo/client/react";
import {
  Box,
  Button as MuiButton,
  DialogContent,
  Divider,
  FormControl,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, Theme, useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import errorIcon from "assets/icons/icon-triangle.svg";
import { Dialog, DialogActions } from "components";
import { useFocusStyles } from "hooks/focusBorder";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store";
import {
  PlaylistPermission,
  SavePlaylistContentInput,
  SavePlaylistParametersInput,
} from "utils/graphql/Global";
import * as PlaylistTypes from "utils/graphql/Playlist";
import { formInputSanitize } from "utils/helpers";
import portalSettingsManager from "utils/portalSettingsManager";
import { ADD_CONTENT_TO_PLAYLIST, PLAYLISTS, SAVE_PLAYLIST } from "utils/queries";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    "& .MuiPaper-root": {
      borderRadius: "0px",
    },
  },
  label: {
    display: "block",
    fontSize: "12px",
    color: theme.palette.grey["500"],
    marginTop: "29px",
    marginBottom: "10px",
  },
  selectPopup: {
    zIndex: 10000,
    "& .k-list-container": {
      backgroundColor: "#ffffff",
    },
    "& .k-list-optionlabel, & .k-list-item": {
      backgroundColor: "#ffffff",
      borderRadius: "0px !important",
      padding: "6px",
      color: "black",
      fontSize: "14px !important",
      "&:hover": {
        backgroundColor: "#E6E6E6 !important",
        border: "2px solid #000000 !important",
      },
    },
  },

  selectStyle: {
    width: "auto",
    height: "32px",
    fontWeight: "500 !important",
    border: "none",
    borderRadius: "0px",
    backgroundColor: "#ffffff",
    "&::placeholder": {
      fontWeight: "500 !important",
      opacity: "0.6 !important",
    },
    "& .k-input-value-text": {
      fontSize: "14px !important",
      fontWeight: "500 !important",
    },
    "&.k-focus": {
      backgroundColor: "#ffffff",
    },
    "&:hover": {
      backgroundColor: "#ffffff",
    },
    "&::after": {
      border: "none !important",
    },
    "& .k-icon-button": {
      padding: "0px 20px !important",
    },
  },
}));

const Button = styled(MuiButton)(({ theme }) => ({
  flex: 1,
  borderRadius: 0,
  color: portalSettingsManager.buttonColors?.themed?.active?.text || theme.palette.primary.main,
  fontSize: 11,
  fontWeight: 700,
  "&.MuiButton-outlined": {
    border: `1px solid ${
      portalSettingsManager.buttonColors?.themed?.active?.border || theme.palette.primary.main
    }`,
  },
  "&.Mui-disabled": {
    color: theme.palette.grey["500"],
  },
}));

const CustomFormControl = withStyles(() => ({
  root: () => ({
    width: "calc(100% - 5%)",
    border: "1px solid black",
    padding: "2px",
    "&:focus-within": {
      border: "2px solid #000000",
    },
  }),
}))(FormControl);

const Clear = withStyles((theme: Theme) => ({
  root: {
    borderRadius: 0,
    color: portalSettingsManager.application?.common?.themeColor,
    fontSize: "12px",
    fontWeight: 700,
    padding: "0px",
    paddingLeft: "22px",
  },
  outlined: {
    border: `1px solid ${portalSettingsManager.application?.common?.themeColor}`,
  },
  disabled: {
    color: theme.palette.grey["500"],
  },
}))(MuiButton);

const PlaylistName = styled("input")(({ theme }) => ({
  marginLeft: "1px",
  padding: "10px 7px",
  width: "calc(100% - 5%)",
  fontFamily: "Arial",
  fontSize: "12px",
  "&:disabled": {
    color: theme.palette.grey["500"],
  },
  "&.error": {
    color: "#d0021b",
  },
}));

const ErrorMessage = styled("div")(({ theme }) => ({
  background: "#d0021b",
  color: "#fff",
  fontSize: 11,
  padding: "5px 20px",
  marginLeft: 2,
}));

const Description = styled("textarea")({
  padding: "10px 10px",
  width: "calc(100% - 20px)",
  fontFamily: "Arial",
  fontSize: "12px",
});

export const PlayListDialog = ({
  open,
  title,
  contentId,
  contentType,
  isLanding,
  reload,
  handleClose,
  description,
}: any): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [newPlayList, setNewPlayList] = useState("");
  const [playlistSummary, setPlaylistSummary] = useState("");
  const [existingPlaylist, setExistingPlaylist] = useState("");
  const [isNewPlaylistSelected, setIsNewPlaylistSelected] = useState(false);
  const [isPlaylistExist, setIsPlaylistExist] = useState(false);
  const [isExistingPlaylisSelected, setIsExistingPlaylisSelected] = useState(false);
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [addContentToPlaylist] = useMutation(ADD_CONTENT_TO_PLAYLIST);
  const [savePlaylist] = useMutation(SAVE_PLAYLIST);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const addToExistingPlaylistSelectBox = "add-to-existing-playlist-select-box";
  const addToExistingPlaylistId = "add-to-existing-playlist-id";

  const validatePlaylist = () => {
    const result = data?.playlists?.filter(
      (item) => item?.title?.toLowerCase().trim() === newPlayList.toLowerCase().trim(),
    );
    let isExist = true;
    if (!result?.length) {
      isExist = false;
      savePlaylistWithContent();
    }
    setIsPlaylistExist(isExist);
  };
  const savePlaylistWithContent = () => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: true, message: t("add_course_to_playlist") },
    });
    const playlistContent: SavePlaylistContentInput = {
      contentId: contentId,
      contentType: contentType,
    };
    const payload: SavePlaylistParametersInput = {
      playlistId: null,
      title: formInputSanitize(newPlayList),
      summary: formInputSanitize(playlistSummary),
      permission: PlaylistPermission.PRIVATE,
      contents: [playlistContent],
    };
    savePlaylist({ variables: { payload: payload } })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("add_course_to_playlist") },
        });
        handleClose();
        if (isLanding) {
          reload(true);
        }
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("playlist_saved_alert_title"),
            message: t("playlist_saved_alert_message"),
          },
        });
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("add_course_to_playlist") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("playlist_saved_alert_title"),
            message: t("playlist_error_alert_message"),
          },
        });
      });
  };
  const playlistAddContent = () => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: true, message: t("add_course_to_playlist") },
    });
    addContentToPlaylist({
      variables: { playlistId: existingPlaylist, contentId: contentId, contentType: contentType },
    })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("add_course_to_playlist") },
        });
        handleClose();
        if (isLanding) {
          reload(true);
        }
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("playlist_content"),
            message: t("playlist_content_add_success_message"),
          },
        });
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("add_course_to_playlist") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("playlist_content"),
            message: t("playlist_content_added_error_message"),
          },
        });
      });
  };

  const onClose = () => {
    handleClose();
  };

  const updatePlayListValues = (value: string) => {
    setExistingPlaylist(value);
    setIsExistingPlaylisSelected(true);
    setIsNewPlaylistSelected(false);
  };

  const onClear = () => {
    setExistingPlaylist("");
    setNewPlayList("");
    setPlaylistSummary("");
    setIsExistingPlaylisSelected(false);
    setIsPlaylistExist(false);
    setIsNewPlaylistSelected(false);
  };

  const onNewPlaylist = (event: any) => {
    setNewPlayList(event.target.value.length > 48 ? newPlayList : event.target.value);
    if (event.target.value.length >= 1) {
      setIsExistingPlaylisSelected(false);
      setIsNewPlaylistSelected(true);
    } else {
      setIsExistingPlaylisSelected(false);
      setIsNewPlaylistSelected(false);
    }
  };

  const { data, loading } = useQuery<PlaylistTypes.Playlist>(PLAYLISTS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });
  const focusClass = useFocusStyles();

  useEffect(() => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: loading, message: t("playlist_loading") },
    });
    if (!loading) {
      const playlistDialog = document.querySelector(`[id='${addToExistingPlaylistSelectBox}']`);
      playlistDialog.children[0].setAttribute("aria-hidden", "true");
      playlistDialog.children[1].setAttribute("aria-hidden", "true");
      playlistDialog.setAttribute("role", "combobox");
      playlistDialog.setAttribute("aria-haspopup", "listbox");
    }
  }, [loading]);

  const existingPlaylistData = data?.playlists
    ? (data.playlists || []).filter(
        (item: PlaylistTypes.Playlist_playlists | null) =>
          !(
            item?.contentIds &&
            item?.contentIds?.length > 1 &&
            item?.contentIds?.includes(contentId)
          ),
      )
    : [];

  const selectedPlaylist = existingPlaylist
    ? existingPlaylistData.find((playlist) => existingPlaylist == playlist.id)
    : "";

  return (
    <Dialog
      id="playlistdialog"
      title={title}
      onClose={() => onClose()}
      role="dialog"
      open={open}
      minWidth={isMobile ? 0 : 430}
      className={classes.root}
      showCloseIcon
      minHeight={isMobile ? 0 : 360}
      aria-modal="true"
    >
      <DialogContent style={{ padding: "8px 35px", minWidth: isMobile ? 0 : 360 }}>
        <Typography
          style={{
            fontSize: "16px",
            color: theme.palette.grey["800"],
            fontWeight: 400,
            paddingBottom: "5px",
          }}
          component="h3"
          id="dialog1_label"
          role="heading"
          aria-level={3}
        >
          {title}
        </Typography>
        <Typography
          style={{
            fontSize: "12px",
            color: theme.palette.grey["800"],
            lineHeight: "18px",
            fontWeight: 400,
          }}
          component="p"
        >
          {description}
        </Typography>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <label
            className={classes.label}
            id={addToExistingPlaylistId}
            htmlFor={addToExistingPlaylistSelectBox}
          >
            {t("add_to_existing_playlist")}
          </label>
          <Clear className={focusClass.focusItem} onClick={onClear}>
            {t("clear").toUpperCase()}
          </Clear>
        </Box>
        <CustomFormControl>
          <DropDownList
            className={classes.selectStyle}
            id={addToExistingPlaylistSelectBox}
            ariaLabelledBy={addToExistingPlaylistId + " " + addToExistingPlaylistSelectBox}
            data={existingPlaylistData}
            dataItemKey="id"
            textField="title"
            value={selectedPlaylist}
            disabled={isNewPlaylistSelected}
            popupSettings={{ className: classes.selectPopup }}
            onChange={(event: any) => updatePlayListValues(event.target.value.id)}
            title={selectedPlaylist ? selectedPlaylist.title : ""}
            onOpen={() => {
              setTimeout(() => {
                const ulListElement = document
                  .querySelector(`[id='${addToExistingPlaylistSelectBox}']`)
                  .getAttribute("aria-owns");
                document
                  .querySelector(`[id='${ulListElement}']`)
                  .setAttribute("aria-labelledby", addToExistingPlaylistId);
              }, 0);
            }}
          />
        </CustomFormControl>

        <Divider style={{ flex: 1, margin: "30px 0px" }} />
        <label className={classes.label} htmlFor="create-new-playlist-id">
          {t("create_new_playlist")}
        </label>
        <PlaylistName
          placeholder=""
          value={newPlayList}
          disabled={isExistingPlaylisSelected}
          className={isPlaylistExist ? "error" : ""}
          onChange={(event) => onNewPlaylist(event)}
          id="create-new-playlist-id"
          type="text"
        />
        {isPlaylistExist ? (
          <ErrorMessage>
            <img style={{ width: 10, marginRight: 5 }} alt={t("error_icon")} src={errorIcon} />
            {t("duplicate_playlist_name_message")}
          </ErrorMessage>
        ) : (
          ""
        )}
        <Box style={{ marginTop: "30px" }}>
          <label className={classes.label} htmlFor="description-id">
            {t("description_optional")}
          </label>
          <Description
            rows={4}
            placeholder=""
            value={playlistSummary}
            disabled={isExistingPlaylisSelected}
            onChange={(event) => setPlaylistSummary(event.target.value)}
            id="description-id"
          />
        </Box>

        <Divider style={{ flex: 1, margin: "30px 0px 3px 0px" }} />
      </DialogContent>

      <DialogActions style={{ padding: "18px 35px 29px 35px" }}>
        <Button
          onClick={() => onClose()}
          style={{
            color:
              portalSettingsManager.buttonColors?.normal?.active?.text || theme.palette.grey["500"],
            maxWidth: "max-content",
            paddingRight: "30px",
          }}
          className={`${focusClass.secondaryHover} ${focusClass.focusItem}`}
        >
          {t("cancel_and_close")}
        </Button>
        <Button
          onClick={() => {
            isExistingPlaylisSelected ? playlistAddContent() : validatePlaylist();
          }}
          color="primary"
          variant="outlined"
          disabled={!(isNewPlaylistSelected || isExistingPlaylisSelected)}
          style={{
            maxWidth: "130px",
            lineHeight: "30px",
            height: "42px",
            color:
              portalSettingsManager.buttonColors?.themed?.active?.text ||
              theme.palette.primary.main,
            border: `1px solid ${
              portalSettingsManager.buttonColors?.themed?.active?.border ||
              theme.palette.primary.main
            }`,
            backgroundColor: portalSettingsManager.buttonColors?.themed?.active?.back,
          }}
          className={`${focusClass.primaryHover} ${focusClass.focusItem}`}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
